import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

interface KBDomainEntry {
  id: string;
  siteId: number | null;
  companyName: string;
  slug: string;
  domain: string;
  domainStatus: string | null;
  domainSetAt: string | null;
  effectiveDomain: string;
}

// Real az values — same profile/RG for both staging and production, different endpoint
const AZ_VARS_STG = `AFD_RG="velaro-eastus-prd-infra-rg"
AFD_PROFILE="velaro-prd-afd"
AFD_ENDPOINT="admin-stg"   # staging endpoint`;

const AZ_VARS_PRD = `AFD_RG="velaro-eastus-prd-infra-rg"
AFD_PROFILE="velaro-prd-afd"
AFD_ENDPOINT="admin-prd"   # production endpoint`;

// Use production vars by default on this page (admin panel is production-facing)
const AZ_VARS = AZ_VARS_PRD;

function azdCommands(slug: string, domain: string) {
  return `# Azure Front Door — production values (change AFD_ENDPOINT to "admin-stg" for staging)
${AZ_VARS}

# 1. Add ${domain} as a custom domain — Front Door provisions SSL automatically
az afd custom-domain create \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --custom-domain-name "kb-${slug}" \\
  --host-name "${domain}" \\
  --minimum-tls-version "TLS12" \\
  --certificate-type "ManagedCertificate"

# 2. Link it to the existing KB route
az afd route update \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --endpoint-name "$AFD_ENDPOINT" \\
  --route-name "kb-admin-route" \\
  --add customDomains "[{id:'kb-${slug}'}]"

# 3. Poll until SSL cert is ready (5–30 min after customer's DNS propagates)
az afd custom-domain show \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --custom-domain-name "kb-${slug}" \\
  --query "domainValidationState"
# → When output is "Approved", click Mark Active above`;
}

const WILDCARD_COMMANDS = `# One-time setup: enable *.kb.livefluence.com on Azure Front Door
# Run once for production, once for staging (swap AFD_ENDPOINT).
# livefluence.com DNS is managed in EasyDNS.

# --- Production ---
${AZ_VARS_PRD}

# --- Staging (run separately with these vars) ---
# AFD_RG="velaro-eastus-prd-infra-rg"
# AFD_PROFILE="velaro-prd-afd"
# AFD_ENDPOINT="admin-stg"

EASYDNS_TOKEN="<your-easydns-api-token>"
EASYDNS_KEY="<your-easydns-api-key>"

# Step 1 — Add wildcard custom domain to Front Door (DigiCert DV cert auto-provisioned)
az afd custom-domain create \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --custom-domain-name "kb-livefluence-wildcard" \\
  --host-name "*.kb.livefluence.com" \\
  --minimum-tls-version "TLS12" \\
  --certificate-type "ManagedCertificate"

# Step 2 — Get the TXT verification value Front Door generated
az afd custom-domain show \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --custom-domain-name "kb-livefluence-wildcard" \\
  --query "validationProperties.validationToken"

# Step 3 — Add wildcard CNAME + TXT ownership record via EasyDNS API
# livefluence.com DNS is in EasyDNS. Get endpoint hostname and TXT value first:
AFD_ENDPOINT_HOSTNAME=$(az afd endpoint show \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --endpoint-name "$AFD_ENDPOINT" \\
  --query "hostName" -o tsv)
VERIFICATION_VALUE="<from step 2 output>"

# Wildcard CNAME: *.kb.livefluence.com → Front Door endpoint
curl -s -X PUT "https://rest.easydns.net/zones/records/livefluence.com" \\
  -u "\${EASYDNS_TOKEN}:\${EASYDNS_KEY}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"host":"*.kb","ttl":3600,"type":"CNAME","data":"'\${AFD_ENDPOINT_HOSTNAME}'"}'

# TXT record for Front Door domain ownership verification
curl -s -X PUT "https://rest.easydns.net/zones/records/livefluence.com" \\
  -u "\${EASYDNS_TOKEN}:\${EASYDNS_KEY}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"host":"_dnsauth.kb","ttl":3600,"type":"TXT","data":"'\${VERIFICATION_VALUE}'"}'

# Step 4 — Poll until cert is approved (5–30 min after DNS propagates)
az afd custom-domain show \\
  --resource-group "$AFD_RG" \\
  --profile-name "$AFD_PROFILE" \\
  --custom-domain-name "kb-livefluence-wildcard" \\
  --query "domainValidationState"
# → "Approved" means *.kb.livefluence.com is live`;

export default function KBDomains() {
  const api = useApi();
  const [tenants, setTenants] = useState<KBDomainEntry[]>([]);
  const [frontDoorEndpoint, setFrontDoorEndpoint] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.admin.get("api/kb/admin/domains");
      if (resp.ok) {
        const data = await resp.json();
        setTenants(Array.isArray(data?.tenants) ? data.tenants : []);
        setFrontDoorEndpoint(data?.frontDoorEndpoint ?? "");
      } else {
        setError("Failed to load KB domains.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function activate(tenantId: string) {
    setActioning(tenantId);
    try {
      const resp = await api.admin.put(`api/kb/admin/domains/${tenantId}/activate`);
      if (resp.ok) await load();
    } finally {
      setActioning(null);
    }
  }

  async function setPending(tenantId: string) {
    setActioning(tenantId);
    try {
      const resp = await api.admin.put(`api/kb/admin/domains/${tenantId}/pending`);
      if (resp.ok) await load();
    } finally {
      setActioning(null);
    }
  }

  const pending = tenants.filter(t => t.domainStatus !== "active");
  const active  = tenants.filter(t => t.domainStatus === "active");

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">KB Custom Domains</h1>
        <p className="text-sm text-slate-500 mt-1">
          Customers who have set a custom domain for their Knowledge Base. Pending = needs Azure Front Door provisioning before it goes live.
        </p>
      </div>

      {/* CNAME target — shown prominently so it's easy to give to customers */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <i className="fa fa-network-wired text-blue-500" />
          <span className="text-sm font-semibold text-slate-800">CNAME Target for All Custom Domains</span>
        </div>
        {frontDoorEndpoint ? (
          <div className="flex items-center gap-3">
            <code className="bg-white border border-blue-200 rounded px-3 py-1.5 text-sm font-mono text-slate-800">
              {frontDoorEndpoint}
            </code>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={() => navigator.clipboard.writeText(frontDoorEndpoint)}
            >
              <i className="fa fa-copy mr-1" />Copy
            </button>
          </div>
        ) : (
          <p className="text-sm text-amber-700">
            <i className="fa fa-triangle-exclamation mr-1" />
            <code>KbFrontDoorEndpoint</code> app setting not set. Add it in Azure App Settings (staging: <code>admin-stg-buhxcyccdrhecxgk.a01.azurefd.net</code>; production: your prod Front Door hostname).
          </p>
        )}
        <p className="text-xs text-slate-500">
          This is what every customer points their CNAME at — same value for all customers. Shown automatically on their KB Settings page once they save a domain.
        </p>
      </div>

      {/* One-time wildcard setup for *.kb.livefluence.com */}
      <details className="rounded-lg border border-amber-200 bg-amber-50">
        <summary className="p-4 cursor-pointer text-sm font-medium text-amber-800 flex items-center gap-2 select-none list-none">
          <i className="fa fa-chevron-right text-xs" />
          One-time setup: enable *.kb.livefluence.com (run once per environment)
        </summary>
        <div className="p-4 pt-2 space-y-2">
          <p className="text-xs text-amber-700">
            This must be done before ANY customer subdomain (<code>slug.kb.livefluence.com</code>) will resolve. Run once for staging, once for production.
          </p>
          <CommandBlock commands={WILDCARD_COMMANDS} />
        </div>
      </details>

      {loading && <p className="text-sm text-slate-400 animate-pulse">Loading…</p>}
      {error && <p className="text-sm text-crimson-red-600">{error}</p>}

      {/* Pending domains */}
      {pending.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
            Pending Provisioning ({pending.length})
          </h2>
          <div className="space-y-3">
            {pending.map(t => (
              <DomainRow
                key={t.id}
                entry={t}
                actioning={actioning}
                onActivate={activate}
                onSetPending={setPending}
              />
            ))}
          </div>
        </section>
      )}

      {active.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
            Active ({active.length})
          </h2>
          <div className="space-y-3">
            {active.map(t => (
              <DomainRow
                key={t.id}
                entry={t}
                actioning={actioning}
                onActivate={activate}
                onSetPending={setPending}
              />
            ))}
          </div>
        </section>
      )}

      {!loading && tenants.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <i className="fa fa-globe text-3xl mb-3 block" />
          <p className="text-sm">No custom domains configured yet.</p>
          <p className="text-xs mt-1">When a customer enters a custom domain in their KB Settings, it appears here.</p>
        </div>
      )}
    </div>
  );
}

function DomainRow({
  entry: t,
  actioning,
  onActivate,
  onSetPending,
}: {
  entry: KBDomainEntry;
  actioning: string | null;
  onActivate: (id: string) => void;
  onSetPending: (id: string) => void;
}) {
  const isPending = t.domainStatus !== "active";
  return (
    <div className={`rounded-lg border ${isPending ? "border-amber-200 bg-white" : "border-green-200 bg-white"}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">{t.companyName || "(no name)"}</span>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
              isPending ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
            }`}>
              {isPending ? "Pending" : "Active"}
            </span>
          </div>
          <div className="text-xs text-slate-500 font-mono">
            {t.domain}
            <span className="text-slate-300 mx-1">→</span>
            <span className="text-slate-400">slug: {t.slug}</span>
            {t.siteId && <span className="text-slate-400 ml-2">· site {t.siteId}</span>}
          </div>
          {t.domainSetAt && (
            <div className="text-xs text-slate-400">
              Set {new Date(t.domainSetAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {isPending ? (
            <button
              onClick={() => onActivate(t.id)}
              disabled={actioning === t.id}
              className="text-xs px-3 py-1.5 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
            >
              {actioning === t.id ? "Saving…" : "Mark Active"}
            </button>
          ) : (
            <button
              onClick={() => onSetPending(t.id)}
              disabled={actioning === t.id}
              className="text-xs px-3 py-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
            >
              {actioning === t.id ? "Saving…" : "Revert to Pending"}
            </button>
          )}
        </div>
      </div>

      {/* Az commands — always expanded for pending, collapsible for active */}
      {isPending ? (
        <div className="border-t border-amber-100 px-4 py-3">
          <p className="text-xs font-medium text-slate-600 mb-2">
            Run these commands, then click Mark Active:
          </p>
          <CommandBlock commands={azdCommands(t.slug, t.domain)} />
        </div>
      ) : (
        <details className="border-t border-green-100">
          <summary className="px-4 py-2 cursor-pointer text-xs text-slate-400 hover:text-slate-600 select-none list-none">
            <i className="fa fa-chevron-right text-[10px] mr-1" />
            Show az commands
          </summary>
          <div className="px-4 pb-3">
            <CommandBlock commands={azdCommands(t.slug, t.domain)} />
          </div>
        </details>
      )}
    </div>
  );
}

function CommandBlock({ commands }: { commands: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="bg-slate-900 text-green-300 rounded p-3 text-[11px] leading-5 overflow-x-auto whitespace-pre font-mono">
        {commands}
      </pre>
      <button
        className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded bg-slate-700 text-slate-300 hover:bg-slate-600"
        onClick={() => {
          navigator.clipboard.writeText(commands);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}
